// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import {
  Check,
  ChevronDown,
  Filter,
  ListFilter,
  Search,
  X,
} from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { SelectOption } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";

export interface FilterSelectPopoverProps {
  content: ReactNode;
  title?: string;
  icon?: ReactNode;
  width?: number | string;
}

export interface FilterSelectProps {
  column: UnifiedTableColumn;
  currentFilter: unknown;
  onFilterChange: (key: string, value: string[] | null) => void;
  onClearFilter: (key: string) => void;
  isTableFilter?: boolean;
  triggerClassName?: string;
  /** Optional size for the trigger button (maps to Button size) */
  triggerSize?: "default" | "sm" | "lg" | "icon";
  /** Optional short label to prefix inside the trigger (e.g., "Statuses") */
  triggerPrefix?: string;
  /** Optional icon to render before the trigger prefix */
  triggerPrefixIcon?: ReactNode;
  /** Text to show in the trigger when no values are selected */
  emptyTriggerText?: string;
  /** Override the value text in the trigger regardless of selection */
  valueTextOverride?: string;
  /** Optional header content rendered at top of popover */
  popoverHeader?: ReactNode;
  /** When true, popover content is visible but non-interactive */
  contentDisabled?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  maxHeight?: number;
  width?: number | string;
  popover?: FilterSelectPopoverProps;
  applyButtonText?: string;
  disableSorting?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional apply handler. Return true to indicate you've handled apply (skip default). */
  onApply?: (
    values: string[],
    opts: { contentDisabled: boolean },
  ) => boolean | undefined;
  /** Called when the user cancels or dismisses (discard) without applying */
  onCancel?: () => void;
}

/**
 * A unified select filter component for multi-select filtering functionality.
 * Works both as a standalone filter and as a table column filter.
 *
 * IMPORTANT: This component currently only supports multi-select filtering with string arrays.
 * For number range filters, date range filters, or text filters, use the FilterPopover component.
 */
export function FilterSelect({
  column,
  currentFilter,
  onFilterChange,
  onClearFilter,
  isTableFilter = true,
  triggerClassName,
  triggerSize = "default",
  triggerPrefix,
  triggerPrefixIcon,
  emptyTriggerText,
  valueTextOverride,
  popoverHeader,
  contentDisabled = false,
  searchPlaceholder = "Search options...",
  emptyMessage = "No options found",
  maxHeight = 200,
  width = 220,
  popover,
  applyButtonText = "Apply Filter",
  disableSorting = false,
  onOpenChange,
  onApply,
  onCancel,
}: FilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [closeArm, setCloseArm] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Removed separate popover open state as InfoTooltip handles its own state

  // Safely convert currentFilter to string array
  const safeCurrentFilter = useMemo(() => {
    return Array.isArray(currentFilter) ? (currentFilter as string[]) : [];
  }, [currentFilter]);

  // Initialize as array even if null
  const [localFilter, setLocalFilter] = useState<string[]>(safeCurrentFilter);

  // Restore the simplified effect that syncs external changes
  useEffect(() => {
    setLocalFilter(
      Array.isArray(currentFilter) ? (currentFilter as string[]) : [],
    );
  }, [currentFilter]);

  // Get label for a given option id
  const getLabelForId = useCallback(
    (id: string): string => {
      if (column.filterOptions) {
        const option = column.filterOptions.find((opt) =>
          typeof opt === "string"
            ? opt === id
            : (opt as SelectOption).id === id,
        );

        if (typeof option === "string") {
          return option;
        } else if (option && "label" in option) {
          return option.label;
        }
      }
      return id;
    },
    [column.filterOptions],
  );

  // Check if filter is active
  const isFilterActive = useMemo(() => {
    return Array.isArray(localFilter) && localFilter.length > 0;
  }, [localFilter]);

  const handleApply = () => {
    // For empty array, pass null to clear the filter
    const valueToApply = localFilter.length === 0 ? null : localFilter;
    if (onApply) {
      const handled = onApply(localFilter, { contentDisabled });
      if (handled) {
        setOpen(false);
        return;
      }
    }
    onFilterChange(column.key, valueToApply);
    setOpen(false);
  };

  const handleCancel = () => {
    // Reset local filter to current filter without applying changes
    setLocalFilter(safeCurrentFilter);
    setOpen(false);
    onCancel?.();
  };

  const handleClear = () => {
    setLocalFilter([]);
    onClearFilter(column.key);
    setOpen(false);
  };

  // Handle popover close - apply changes when clicking outside
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open) {
      if (!closeArm) {
        // First outside click: keep open, arm close without applying
        setCloseArm(true);
        setOpen(true);
        return;
      }
      // Second outside click: close and discard changes
      setCloseArm(false);
      setOpen(false);
      // Reset local changes
      setLocalFilter(safeCurrentFilter);
      if (onOpenChange) onOpenChange(false);
      onCancel?.();
      return;
    }
    setCloseArm(false);
    setOpen(newOpen);
    if (onOpenChange) onOpenChange(newOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleSelectOption = (optionId: string) => {
    // Toggle selection
    if (localFilter.includes(optionId)) {
      setLocalFilter(localFilter.filter((item) => item !== optionId));
    } else {
      setLocalFilter([...localFilter, optionId]);
    }
  };

  // Format the display text for the selected options summary
  const getSelectedSummary = () => {
    if (localFilter.length === 0) {
      return (
        <div className="text-sm font-medium mt-2 h-5 invisible">
          Selected: placeholder
        </div>
      );
    } else if (localFilter.length === 1) {
      return (
        <div className="text-sm font-medium mt-2 h-5">
          Selected: {getLabelForId(localFilter[0])}
        </div>
      );
    } else {
      return (
        <div className="text-sm font-medium mt-2 h-5">
          Selected: {localFilter.length} items
        </div>
      );
    }
  };

  // Process filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!column.filterOptions) return [];

    let results = column.filterOptions.filter((option) => {
      const label = typeof option === "string" ? option : option.label;
      return label?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    // Sort alphabetically unless sorting is disabled
    if (!disableSorting) {
      results = results.slice().sort((a, b) => {
        const labelA = typeof a === "string" ? a : a.label;
        const labelB = typeof b === "string" ? b : b.label;
        return labelA && labelB ? labelA.localeCompare(labelB) : 0;
      });
    }

    return results;
  }, [column.filterOptions, searchQuery, disableSorting]);

  // Render table filter button or standard trigger
  const renderTrigger = () => {
    if (isTableFilter) {
      return (
        <Button
          variant={isFilterActive ? "outline" : "ghost"}
          size="icon"
          className={cn(
            "h-6 w-6 p-0",
            isFilterActive ? "text-primary" : "text-muted-foreground",
            triggerClassName,
          )}
        >
          {isFilterActive ? (
            <Filter className="h-3 w-3" />
          ) : (
            <ListFilter className="h-3 w-3" />
          )}
        </Button>
      );
    }

    // For non-table usage, implement a dropdown style button similar to MultiSelect
    const buttonWidth = typeof width === "number" ? `${width}px` : width;

    return (
      <Button
        variant="outline"
        size={triggerSize}
        role="combobox"
        aria-expanded={open}
        className={cn("justify-between", triggerClassName)}
        style={{
          width: buttonWidth,
        }}
      >
        <div className="flex items-center gap-2 overflow-hidden">
          <span className="truncate flex items-center gap-1.5">
            {triggerPrefix && (
              <span className="flex items-center gap-1 text-muted-foreground">
                {triggerPrefixIcon}
                <span>{triggerPrefix}:</span>
              </span>
            )}
            <span
              className={cn(
                "truncate",
                // Primary green when active, red when explicitly showing empty state text
                isFilterActive
                  ? "text-primary"
                  : emptyTriggerText
                    ? "text-destructive"
                    : undefined,
              )}
            >
              {valueTextOverride !== undefined && valueTextOverride !== null
                ? valueTextOverride
                : isFilterActive
                  ? localFilter.length === 1
                    ? getLabelForId(localFilter[0])
                    : localFilter.length > 1
                      ? `${getLabelForId(localFilter[0])} +${
                          localFilter.length - 1
                        }`
                      : column.title
                  : (emptyTriggerText ?? column.title)}
            </span>

            {popover && (
              <InfoTooltip
                trigger={
                  <span
                    className="inline-flex h-4 w-4 cursor-pointer items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {popover.icon || (
                      <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                    )}
                  </span>
                }
                side="bottom"
                align="start"
                className="text-sm"
                style={{ width: popover.width || "16rem" }}
              >
                <div className="space-y-2">
                  {popover.title && (
                    <h4 className="font-medium">{popover.title}</h4>
                  )}
                  {popover.content}
                </div>
              </InfoTooltip>
            )}
          </span>
        </div>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    );
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      {open && (
        <PopoverContent
          align="end"
          className={
            column.filterWidth ? `w-[${column.filterWidth}px]` : "w-80"
          }
          onKeyDown={handleKeyDown}
        >
          <div className="space-y-4">
            {popoverHeader}
            {popoverHeader && (
              <div className="border-b border-border/40 -mx-1" />
            )}
            {!contentDisabled && (
              <div className="mb-2" onKeyDown={handleKeyDown}>
                <div className="relative mb-2">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={searchPlaceholder}
                    onKeyDown={(e) => e.stopPropagation()}
                    value={searchQuery}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      !contentDisabled && setSearchQuery(e.target.value)
                    }
                    disabled={contentDisabled}
                    className="pl-8 pr-8"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="flex justify-start mb-1 text-xs">
                  <button
                    className="text-muted-foreground hover:text-foreground px-1"
                    disabled={contentDisabled}
                    onClick={(e) => {
                      e.preventDefault();
                      if (contentDisabled) return;
                      // Select all currently visible options
                      const visibleOptionIds = filteredOptions.map((option) =>
                        typeof option === "string"
                          ? option
                          : (option as SelectOption).id,
                      );
                      setLocalFilter(visibleOptionIds);
                    }}
                  >
                    All
                  </button>
                  <span className="text-muted-foreground mx-1">|</span>
                  <button
                    className="text-muted-foreground hover:text-foreground px-1"
                    disabled={contentDisabled}
                    onClick={(e) => {
                      e.preventDefault();
                      if (contentDisabled) return;
                      // Deselect all currently visible options
                      const visibleOptionIds = filteredOptions.map((option) =>
                        typeof option === "string"
                          ? option
                          : (option as SelectOption).id,
                      );
                      setLocalFilter(
                        localFilter.filter(
                          (id) => !visibleOptionIds.includes(id),
                        ),
                      );
                    }}
                  >
                    None
                  </button>
                </div>

                <div
                  className="h-[200px] overflow-y-auto overflow-x-hidden rounded-md border p-1 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-thumb]:rounded-md"
                  style={{ height: `${maxHeight}px` }}
                  onWheel={(e) => e.stopPropagation()}
                >
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => {
                      const optionId =
                        typeof option === "string"
                          ? option
                          : (option as SelectOption).id;
                      const optionLabel =
                        typeof option === "string" ? option : option.label;
                      const isSelected = localFilter.includes(optionId);

                      return (
                        <div
                          key={optionId}
                          className={cn(
                            "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground group",
                            isSelected && "bg-accent/50",
                            contentDisabled && "opacity-60 cursor-default",
                          )}
                          onClick={() => {
                            if (contentDisabled) return;
                            handleSelectOption(optionId);
                          }}
                        >
                          <div
                            className={cn(
                              "mr-2 h-4 w-4 flex-shrink-0 rounded-sm border flex items-center justify-center self-center",
                              isSelected
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-primary/20",
                            )}
                          >
                            {isSelected && <Check className="h-3 w-3" />}
                          </div>
                          <div className="relative flex-grow min-w-0 pr-14">
                            <span className="break-words">{optionLabel}</span>
                            <button
                              className="absolute right-0 top-1/2 transform -translate-y-1/2 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity px-2 py-0.5 hover:bg-secondary hover:text-foreground rounded hover:shadow-sm flex-shrink-0 bg-accent/30"
                              disabled={contentDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (contentDisabled) return;
                                const valueToApply = [optionId];
                                // Apply the filter directly
                                onFilterChange(column.key, valueToApply);
                                // Close the popover
                                setOpen(false);
                                // Update local state for consistency (optional but good practice)
                                setLocalFilter(valueToApply);
                              }}
                            >
                              only
                            </button>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="py-2 px-2 text-sm text-muted-foreground">
                      {emptyMessage}
                    </div>
                  )}
                </div>

                {getSelectedSummary()}
              </div>
            )}
            <div className="flex justify-between">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                disabled={contentDisabled}
              >
                Clear
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleApply}>
                  {applyButtonText}
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}

/**
 * Standalone version of FilterSelect for use outside of tables
 */
export interface StandaloneFilterSelectProps {
  options: string[] | SelectOption[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  label?: string;
  triggerPrefix?: string;
  triggerPrefixIcon?: ReactNode;
  emptyTriggerText?: string;
  valueTextOverride?: string;
  popoverHeader?: ReactNode;
  contentDisabled?: boolean;
  onApply?: (
    values: string[],
    opts: { contentDisabled: boolean },
  ) => boolean | undefined;
  onCancel?: () => void;
  emptyMessage?: string;
  searchPlaceholder?: string;
  triggerClassName?: string;
  maxHeight?: number;
  width?: number | string;
  popover?: FilterSelectPopoverProps;
  applyButtonText?: string;
  disableSorting?: boolean;
  resetToDefaults?: () => string[];
}

export function StandaloneFilterSelect({
  options,
  selectedValues = [],
  onSelectionChange,
  label = "Filter",
  triggerPrefix,
  triggerPrefixIcon,
  emptyTriggerText,
  valueTextOverride,
  popoverHeader,
  contentDisabled,
  onApply,
  onCancel,
  emptyMessage = "No options found",
  searchPlaceholder = "Search options...",
  triggerClassName,
  maxHeight = 200,
  width = 220,
  popover,
  applyButtonText,
  disableSorting,
}: StandaloneFilterSelectProps) {
  // Create a virtual column for compatibility with FilterSelect
  const column = {
    key: "standalone",
    title: label,
    filterOptions: options,
  };

  const handleFilterChange = (_key: string, value: string[] | null) => {
    onSelectionChange(value || []);
  };

  const handleClearFilter = () => {
    onSelectionChange([]);
  };

  return (
    <FilterSelect
      column={column}
      currentFilter={selectedValues}
      onFilterChange={handleFilterChange}
      onClearFilter={handleClearFilter}
      isTableFilter={false}
      triggerClassName={triggerClassName}
      triggerPrefix={triggerPrefix}
      triggerPrefixIcon={triggerPrefixIcon}
      emptyTriggerText={emptyTriggerText}
      valueTextOverride={valueTextOverride}
      popoverHeader={popoverHeader}
      contentDisabled={contentDisabled}
      onApply={onApply}
      onCancel={onCancel}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      maxHeight={maxHeight}
      width={width}
      popover={popover}
      applyButtonText={applyButtonText}
      disableSorting={disableSorting}
    />
  );
}
